/**
 * Created by WorkAxis www.workaxis.in
 * WorkAxis — AI & product engineering partner.
 */
import React from 'react';
import "./style.css";
import {Link} from "react-router-dom";
import {
    serviceBuckets,
    idealCustomers,
    clientWork,
    clients,
    testimony,
    antcrmProofPoints
} from "./data";
import {useReveal, CtaSection} from "./shared";
import Hero from "./Hero/Hero";

export default function Landing() {
    useReveal();

    return (
        <div>
            <Hero/>

            {/* ---------- Who we help ---------- */}
            <section className="wx-section wx-section-tight" id="who-we-help">
                <div className="wx-container">
                    <div className="wx-section-head wx-reveal">
                        <div className="wx-kicker">Who we help</div>
                        <h2 className="wx-h2">Built for teams that need to move faster without a large engineering org.</h2>
                    </div>
                    <div className="wx-services-grid is-three">
                        {idealCustomers.map((seg, i) => (
                            <div key={seg.name} className="wx-service-card wx-reveal"
                                 style={{transitionDelay: `${i * 90}ms`}}>
                                <div className="wx-service-title">{seg.name}</div>
                                <div className="wx-service-desc">{seg.message}</div>
                                <ul className="wx-product-points">
                                    {seg.signals.map((s) => <li key={s}>{s}</li>)}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- Services ---------- */}
            <section className="wx-section" id="services">
                <div className="wx-container">
                    <div className="wx-section-head wx-reveal">
                        <div className="wx-kicker">Services</div>
                        <h2 className="wx-h2">Four ways we partner with you.</h2>
                        <p className="wx-lead">
                            If a process is manual, fragmented, or hard to scale, we map it, design the right
                            system, and build it into your stack — not another off-the-shelf tool you have to bend around.
                        </p>
                    </div>
                    <div className="wx-services-grid is-four-buckets">
                        {serviceBuckets.map((s, i) => (
                            <div key={s.name} className="wx-service-card wx-reveal"
                                 style={{transitionDelay: `${(i % 2) * 90}ms`}}>
                                <div className="wx-service-index">{String(i + 1).padStart(2, '0')} /</div>
                                <div className="wx-service-title">{s.name}</div>
                                <div className="wx-service-desc">{s.tagline}</div>
                                <p className="wx-bucket-desc">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                    <div className="wx-section-link wx-reveal">
                        <Link to="/services" className="wx-btn wx-btn-ghost">Explore how we work →</Link>
                    </div>
                </div>
            </section>

            {/* ---------- Products ---------- */}
            <section className="wx-section" id="products">
                <div className="wx-container">
                    <div className="wx-section-head wx-reveal">
                        <div className="wx-kicker">Products & Work</div>
                        <h2 className="wx-h2">We build software like a product company — not a ticket factory.</h2>
                        <p className="wx-lead">
                            From idea to production — we design, build, and operate software that businesses depend on.
                        </p>
                    </div>

                    <div className="wx-product-featured wx-reveal">
                        <div className="wx-product-featured-body">
                            <div className="wx-product-tag">Our product · Proof we ship</div>
                            <h3>AntCRM</h3>
                            <p>
                                Our customer relationship platform — designed, engineered, and operated entirely
                                in-house. We eat our own dog food: the same product discipline we bring to client work.
                            </p>
                            <ul className="wx-product-points">
                                {antcrmProofPoints.map((p) => <li key={p}>{p}</li>)}
                            </ul>
                            <a href="https://antcrm.co" target="_blank" rel="noreferrer"
                               className="wx-btn wx-btn-primary">Explore AntCRM</a>
                        </div>
                        <div className="wx-product-featured-visual">
                            <div className="wx-ant-mark">
                                <div className="wx-ant-logo wx-grad-text">AntCRM</div>
                                <div className="wx-ant-domain">antcrm.co</div>
                            </div>
                        </div>
                    </div>

                    <div className="wx-products-grid">
                        {clientWork.map((product, i) => (
                            <a href={product.link} target="_blank" rel="noreferrer" key={product.name}
                               className="wx-product-card wx-reveal" style={{transitionDelay: `${i * 90}ms`}}>
                                <div className="wx-product-card-image">
                                    <img loading="lazy" src={product.image} alt={product.name}/>
                                </div>
                                <div className="wx-product-card-body">
                                    <h4>{product.name}</h4>
                                    <p className="wx-case-problem">{product.problem}</p>
                                    <p className="wx-case-ownership"><strong>Ownership:</strong> {product.ownership}</p>
                                </div>
                            </a>
                        ))}
                    </div>
                    <div className="wx-section-link wx-reveal">
                        <Link to="/products" className="wx-btn wx-btn-ghost">View case studies →</Link>
                    </div>
                </div>
            </section>

            {/* ---------- Clients ---------- */}
            <section className="wx-section wx-section-tight">
                <div className="wx-container">
                    <div className="wx-section-head is-center wx-reveal">
                        <div className="wx-kicker">Trusted by</div>
                    </div>
                    <div className="wx-clients-row wx-reveal">
                        {clients.map((client) => (
                            <img key={client.name} src={client.image} loading="lazy" alt={client.name}/>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- Testimonials ---------- */}
            <section className="wx-section wx-section-tight">
                <div className="wx-container">
                    <div className="wx-section-head is-center wx-reveal">
                        <div className="wx-kicker">Testimonials</div>
                        <h2 className="wx-h2">What partners say.</h2>
                    </div>
                </div>
                <div className="wx-marquee">
                    {[0, 1].map((dup) => (
                        <div className="wx-marquee-track" aria-hidden={dup === 1} key={dup}>
                            {testimony.map((t) => (
                                <div className="wx-quote-card" key={dup + t.name}>
                                    <div className="wx-quote-text">{t.command}</div>
                                    <div className="wx-quote-person">
                                        <div className="wx-quote-name">{t.name}</div>
                                        <div className="wx-quote-role">{t.designation}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            <CtaSection/>
        </div>
    );
}
